import { useCallback, useState } from "react";
import { AppDispatch } from "@/store";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchVacancies,
  selectVacancies,
  selectVacanciesLoading,
  selectVacanciesError,
  updateVacancy,
  updateVacancyStatus,
  createVacancy,
  notifyClosedVacancyCandidates,
  deleteVacancy,
  postVacancyToCareers24 as postToCareers24Thunk,
} from "../vacancies/store/vacancySlice";
import { VacancyStatus } from "../enums/VacancyStatus";
import { selectCurrentOrganization } from "@/modules/organizations/store/organizationSlice";
import { CreateVacancyCommand } from "../dtos/CreateVacancyDto";

export const useVacancies = () => {
  const dispatch = useDispatch<AppDispatch>();
  const currentOrganization = useSelector(selectCurrentOrganization);
  const vacancies = useSelector(selectVacancies);
  const isLoading = useSelector(selectVacanciesLoading);
  const error = useSelector(selectVacanciesError);

  const [isCareers24Loading, setIsCareers24Loading] = useState(false);

  const loadVacancies = useCallback(
    (searchTerm?: string, status?: VacancyStatus | "All") => {
      return dispatch(
        fetchVacancies({
          organizationId: currentOrganization?.id,
          searchTerm,
          status,
        }),
      ).unwrap();
    },
    [dispatch, currentOrganization?.id],
  );

  const createNewVacancy = useCallback(
    async (formData: CreateVacancyCommand) => {
      formData.organizationId = currentOrganization?.id || "";
      await dispatch(createVacancy({ ...formData })).unwrap();

      await loadVacancies();
    },
    [dispatch, currentOrganization?.id, loadVacancies],
  );

  const notifyRegretCandidates = useCallback(
    async (vacancyId: string) => {
      const organizationId = currentOrganization?.id || "";

      const emailsSent = await dispatch(
        notifyClosedVacancyCandidates({
          organizationId,
          vacancyId,
        }),
      ).unwrap();

      return emailsSent;
    },
    [dispatch, currentOrganization?.id],
  );

  const updateVacancyList = useCallback(
    async (vacancyId: string, formData: CreateVacancyCommand) => {
      formData.organizationId = currentOrganization?.id || "";
      await dispatch(updateVacancy({ ...formData, vacancyId })).unwrap();

      await loadVacancies();
    },
    [dispatch, currentOrganization?.id, loadVacancies],
  );
  const updateVacancyStatusById = useCallback(
    async (vacancyId: string, status: VacancyStatus) => {
      const organizationId = currentOrganization?.id || "";

      await dispatch(
        updateVacancyStatus({
          organizationId,
          vacancyId,
          status,
        }),
      ).unwrap();

      await loadVacancies();
    },
    [dispatch, currentOrganization?.id, loadVacancies],
  );

  const deleteVacancyById = useCallback(
    async (vacancyId: string) => {
      const organizationId = currentOrganization?.id || "";
      await dispatch(
        deleteVacancy({
          organizationId,
          id: vacancyId,
        }),
      ).unwrap();

      await loadVacancies();
    },
    [dispatch, currentOrganization?.id, loadVacancies],
  );

  const postVacancyToCareers24 = useCallback(
    async (vacancyId: string): Promise<{ success: boolean; message?: string; vacancyUrl?: string }> => {
      const organizationId = currentOrganization?.id || "";
      setIsCareers24Loading(true)
      try {
        return await dispatch(
          postToCareers24Thunk({ organizationId, vacancyId }),
        ).unwrap();
      } finally {
        setIsCareers24Loading(false)
      }
    },
    [dispatch, currentOrganization?.id],
  );

  return {
    vacancies,
    isLoading,
    isCareers24Loading,
    error,
    loadVacancies,
    updateVacancyList,
    updateVacancyStatusById,
    createNewVacancy,
    notifyRegretCandidates,
    deleteVacancyById,
    postVacancyToCareers24,
  };
};
